import { createAnalyticsDispatcher, analyticsPath } from '~/utils/analytics-core.mjs'
import { createClarityConsent } from '~/utils/clarity-consent.mjs'

export default defineNuxtPlugin(app => {
  const config = useRuntimeConfig().public
  const enabled = config.analyticsEnabled === true && config.analyticsPropertyReady === true && /^G-[A-Z0-9]+$/.test(String(config.analyticsMeasurementId || ''))
  const available = useState('analytics-available', () => false)
  const choice = useState<'unknown' | 'granted' | 'denied'>('analytics-choice', () => 'unknown')
  available.value = enabled
  if (!enabled) return
  const clarity = createClarityConsent(window, document)
  const id = String(config.analyticsMeasurementId), key = 'musicmint:analytics-consent:v1'
  const win = window as any
  let loading = false, loaded = false, configured = false
  function raw(..._args: any[]) { (win.dataLayer ||= []).push(arguments) }
  const page = () => analyticsPath(app.$router.currentRoute.value.path)
  const dispatcher = createAnalyticsDispatcher({send(name: string, params: Record<string, unknown>) {
    raw('event', name, {...params, page_location: window.location.origin + (params.page_path || page()), page_title: 'MusicMint', page_referrer: ''})
  }})
  // Legacy components cannot bypass the same consent and parameter filtering gates.
  window.gtag = (command: string, name: string, params: Record<string, unknown>) => { if (command === 'event') dispatcher.track(name, {page_path:page(), ...params}) }
  function activate() {
    if (!loaded || choice.value !== 'granted') return
    win[`ga-disable-${id}`] = false
    raw('consent', 'update', {analytics_storage:'granted', ad_storage:'denied', ad_user_data:'denied', ad_personalization:'denied'})
    if (!configured) {
      raw('js', new Date())
      raw('config', id, {send_page_view:false, allow_google_signals:false, allow_ad_personalization_signals:false, page_location:window.location.origin + page(), page_title:'MusicMint', page_referrer:'', ignore_referrer:true})
      configured = true
    }
    dispatcher.setReady(true)
  }
  function load() {
    if (loaded) { activate(); return }
    if (loading) return
    loading = true
    const script = document.createElement('script')
    script.async = true
    script.referrerPolicy = 'no-referrer'
    script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`
    script.onload = () => { loaded = true; loading = false; activate() }
    script.onerror = () => { loading = false; script.remove() }
    document.head.appendChild(script)
  }
  function apply(value: string, persist = true) {
    const next = value === 'granted' ? 'granted' : value === 'denied' ? 'denied' : 'unknown'
    choice.value = next
    if (persist) { try { localStorage.setItem(key, next) } catch { /* This tab still honors the choice. */ } }
    dispatcher.setConsent(next)
    clarity.setConsent(next)
    if (next === 'granted') {
      dispatcher.page(app.$router.currentRoute.value.path)
      load()
    } else {
      win[`ga-disable-${id}`] = true
      dispatcher.setReady(false)
      if (loaded) raw('consent', 'update', {analytics_storage:'denied', ad_storage:'denied', ad_user_data:'denied', ad_personalization:'denied'})
      try { sessionStorage.removeItem('musicmint:analytics-success:v1') } catch { /* no persistence */ }
    }
  }
  win[`ga-disable-${id}`] = true
  raw('consent', 'default', {analytics_storage:'denied', ad_storage:'denied', ad_user_data:'denied', ad_personalization:'denied'})
  window.musicmintAnalytics = {track:(name, params) => dispatcher.track(name, {page_path:page(), ...params}), setConsent:apply, granted:()=>choice.value === 'granted'}
  let saved = 'unknown'
  try { saved = localStorage.getItem(key) || 'unknown' } catch { /* Default deny. */ }
  apply(saved, false)
  app.$router.afterEach((to, _from, failure) => { if (!failure) dispatcher.page(to.path) })
  window.addEventListener('storage', event => { if (event.key === key) apply(event.newValue || 'unknown', false) })
})
